import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { useWorkoutActions } from '~/workout/hooks/useWorkoutActions';
import type { WorkoutRecord } from '~/powersync/generated/schema';

export interface WorkoutStartStopButtonProps {
  workout: WorkoutRecord;
  className?: string;
}

/**
 * Subtle, full-width "Start Workout" control for the workout builder.
 *
 * Only shown for PLANNED workouts (clones the template and navigates to
 * progress). Once the workout is STARTED or FINISHED it is hidden — the
 * timer/stop control lives in the header from then on.
 */
export const WorkoutStartStopButton = ({
  workout,
  className,
}: WorkoutStartStopButtonProps) => {
  const { t } = useTranslation('workout');
  const { startWorkout, isStarting } = useWorkoutActions();

  // Only the planned (idle) state shows a start CTA.
  if (
    WorkoutUtils.getCompletionStatus(workout) !==
    WorkoutCompletionStatus.PLANNED
  ) {
    return null;
  }

  return (
    <Button
      testID="workout-start-button"
      variant="primary"
      size="sm"
      onPress={() => startWorkout(workout.id)}
      loading={isStarting}
      className={className}
    >
      <View className="flex-row items-center justify-center gap-2">
        <Icon name="Play" size={16} />
        <Text>{t('form.buttons.startWorkout')}</Text>
      </View>
    </Button>
  );
};
